import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { ParkingArea, ParkingAreaInput } from '../../types/parking';
import { ParkingAreaKind, ParkingBlueprintSource } from '../../types/Enums';
import { translate, translateParkingAreaKind } from '../../types/EnumTranslations';
import { BuildingStructure } from '../../types/protocols';
import { LevelImpl, RoomImpl } from '../../types/implementations';
import { RoomCreateInput } from '../../api/roomsApi';
import {
    drawingChangeConfirmationText,
    isLevelBlueprintSource,
    roomsOnLevel,
    spotsHiddenByDrawingChange,
} from '../../Helpers/parkingUtils';
import RoomCreateModal from './RoomCreateModal';

interface ParkingAreaModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: ParkingAreaInput) => Promise<void>;
    /** When given, the modal edits this area; otherwise it creates one. */
    area?: ParkingArea;
    /** Building structure (levels and rooms) for the level → room picker. */
    structure: BuildingStructure | undefined;
    /** "Skapa rum": writes the room, refetches the building, returns the key. */
    onCreateRoom: (input: RoomCreateInput) => Promise<string>;
    submitting?: boolean;
}

/** Select value for a stored room that no longer exists (forces a new pick). */
const MISSING_ROOM = '__missing__';

/**
 * Create/edit form for a parking area. Ceiling height is entered in mm and stored in metres.
 * Garages pick a level (a filter, not stored) and then a room on it.
 * `blueprintSource` is never part of the form; a confirmation is shown when the
 * save would change the resolved drawing while spots are placed.
 */
const ParkingAreaModal: React.FC<ParkingAreaModalProps> = ({
    show, onHide, onSubmit, area, structure, onCreateRoom, submitting = false,
}) => {
    const isEditing = !!area;
    const [name, setName] = useState('');
    const [kind, setKind] = useState<ParkingAreaKind>(ParkingAreaKind.Garage);
    const [levelKey, setLevelKey] = useState('');
    const [roomKey, setRoomKey] = useState('');
    const [ceilingHeightMm, setCeilingHeightMm] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [confirmHiddenCount, setConfirmHiddenCount] = useState<number | null>(null);
    const [showRoomCreate, setShowRoomCreate] = useState(false);

    // The area object gets a new identity on every building refetch (e.g. after
    // "Skapa rum"); read it through a ref so the form only resets when opened.
    const areaRef = useRef(area);
    areaRef.current = area;
    const structureRef = useRef(structure);
    structureRef.current = structure;

    useEffect(() => {
        if (!show) return;
        const current = areaRef.current;
        const rooms = structureRef.current?.rooms || {};
        setName(current?.name || '');
        setKind(current?.kind || ParkingAreaKind.Garage);
        if (current?.roomKey && rooms[current.roomKey]) {
            setRoomKey(current.roomKey);
            setLevelKey(rooms[current.roomKey].levelKey || '');
        } else {
            setRoomKey(current?.roomKey ? MISSING_ROOM : '');
            setLevelKey('');
        }
        setCeilingHeightMm(current?.defaultCeilingHeight ? String(Math.round(current.defaultCeilingHeight * 1000)) : '');
        setComment(current?.comment || '');
        setError(null);
        setConfirmHiddenCount(null);
        setShowRoomCreate(false);
    }, [show, area?.key]);

    const sortedLevels = useMemo(() => Object.values(structure?.levels || {})
        .map(level => new LevelImpl(level))
        .sort((a, b) => b.number - a.number), [structure]);

    const levelRooms = useMemo(() => roomsOnLevel(structure, levelKey).map(room => new RoomImpl(room)), [structure, levelKey]);
    const selectedLevel = sortedLevels.find(level => level.key === levelKey);
    const isGarage = kind === ParkingAreaKind.Garage;
    const storedIsLevelMode = !!area && isLevelBlueprintSource(area);

    const handleLevelChange = (value: string) => {
        setLevelKey(value);
        // Changing the level clears the room.
        setRoomKey('');
        setConfirmHiddenCount(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!name.trim()) {
            setError('Namn krävs');
            return;
        }
        if (isGarage && roomKey === MISSING_ROOM) {
            setError('Rummet finns inte längre. Välj ett nytt rum eller "Inget rum".');
            return;
        }
        let defaultCeilingHeight: number | undefined;
        if (ceilingHeightMm.trim()) {
            const mm = parseInt(ceilingHeightMm, 10);
            if (!Number.isFinite(mm) || mm <= 0) {
                setError('Takhöjd måste vara ett positivt antal millimeter');
                return;
            }
            defaultCeilingHeight = mm / 1000;
        }

        const nextRoomKey = isGarage && roomKey && roomKey !== MISSING_ROOM ? roomKey : undefined;

        // Compare the resolved drawing before and after; updateArea forces
        // 'own' when level mode loses its room or garage kind.
        if (area && confirmHiddenCount === null) {
            const forcesOwn = storedIsLevelMode && (!nextRoomKey || !isGarage);
            const after = {
                ...area,
                kind,
                roomKey: nextRoomKey,
                blueprintSource: forcesOwn ? ParkingBlueprintSource.Own : area.blueprintSource,
            };
            const hidden = spotsHiddenByDrawingChange(area, after, structure);
            if (hidden > 0) {
                setConfirmHiddenCount(hidden);
                return;
            }
        }

        try {
            await onSubmit({
                name: name.trim(),
                kind,
                roomKey: nextRoomKey,
                defaultCeilingHeight,
                comment: comment.trim() || undefined,
            });
            onHide();
        } catch (err) {
            setConfirmHiddenCount(null);
            setError(err instanceof Error ? err.message : 'Kunde inte spara parkeringsområdet');
        }
    };

    return (
        <>
            <Modal show={show && !showRoomCreate} onHide={onHide} centered>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditing ? 'Redigera parkeringsområde' : 'Nytt parkeringsområde'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="T.ex. Garage plan -1"
                                autoFocus
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Typ</Form.Label>
                            <Form.Select
                                value={kind}
                                onChange={e => { setKind(e.target.value as ParkingAreaKind); setConfirmHiddenCount(null); }}
                            >
                                {Object.values(ParkingAreaKind).map(value => (
                                    <option key={value} value={value}>{translateParkingAreaKind(value)}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {isGarage && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Våning</Form.Label>
                                    <Form.Select value={levelKey} onChange={e => handleLevelChange(e.target.value)}>
                                        <option value="">Välj våning</option>
                                        {sortedLevels.map(level => (
                                            <option key={level.key} value={level.key}>{level.displayName()}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text muted>Välj våning för att kunna välja rum.</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Rum</Form.Label>
                                    {roomKey === MISSING_ROOM && (
                                        <Alert variant="warning" className="py-2 mb-2">Rummet finns inte längre. Välj ett nytt rum.</Alert>
                                    )}
                                    <div className="d-flex gap-2 align-items-start flex-wrap">
                                        <Form.Select
                                            value={roomKey}
                                            onChange={e => { setRoomKey(e.target.value); setConfirmHiddenCount(null); }}
                                            disabled={!levelKey && roomKey !== MISSING_ROOM}
                                            style={{ flex: '1 1 200px' }}
                                        >
                                            {roomKey === MISSING_ROOM && (
                                                <option value={MISSING_ROOM} disabled>Rummet finns inte längre</option>
                                            )}
                                            <option value="">Inget rum</option>
                                            {levelRooms.map(room => (
                                                <option key={room.key} value={room.key}>
                                                    {room.displayName()}{room.type && room.displayName() !== translate(room.type) ? ` (${translate(room.type)})` : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => setShowRoomCreate(true)}
                                            disabled={!levelKey || submitting}
                                            title={levelKey ? undefined : 'Välj våning först'}
                                        >
                                            <Plus size={14} className="me-1" />Skapa rum
                                        </Button>
                                    </div>
                                    <Form.Text muted>
                                        Om platserna ligger i flera rum, skapa ett parkeringsområde per rum.
                                        {storedIsLevelMode && ' Utan rum slutar området använda våningens ritning.'}
                                    </Form.Text>
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Förinställd fri höjd (mm)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={ceilingHeightMm}
                                onChange={e => setCeilingHeightMm(e.target.value)}
                                placeholder="T.ex. 2100"
                            />
                            <Form.Text muted>Används för platser som saknar egen höjd.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <Form.Label>Kommentar</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                            />
                        </Form.Group>

                        {confirmHiddenCount !== null && (
                            <Alert variant="warning" className="mt-3 mb-0">
                                {drawingChangeConfirmationText(confirmHiddenCount)} Spara ändå?
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                        <Button type="submit" variant={confirmHiddenCount !== null ? 'warning' : 'primary'} disabled={submitting}>
                            {submitting
                                ? <><Spinner size="sm" animation="border" /> Sparar...</>
                                : confirmHiddenCount !== null ? 'Spara ändå' : 'Spara'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {isGarage && levelKey && (
                <RoomCreateModal
                    show={show && showRoomCreate}
                    onHide={() => setShowRoomCreate(false)}
                    levelKey={levelKey}
                    levelName={selectedLevel?.displayName() || ''}
                    onSubmit={onCreateRoom}
                    onCreated={(newRoomKey) => {
                        setRoomKey(newRoomKey);
                        setConfirmHiddenCount(null);
                        setShowRoomCreate(false);
                    }}
                    submitting={submitting}
                />
            )}
        </>
    );
};

export default ParkingAreaModal;
